import api from "./axiosInstance";

export const DASHBOARD_RESULTS_SUBJECTS_PATH = "/v1/dashboard/results/subjects";
export const DASHBOARD_RESULTS_SUBJECT_DETAIL_PATH =
  "/v1/dashboard/results/subjects/detail";
export const DASHBOARD_RESULTS_RECORDINGS_PATH_PREFIX =
  "/v1/dashboard/results/recordings";
export const DASHBOARD_RESULTS_EXPORT_PATH = "/v1/dashboard/results/export";
export const DASHBOARD_SUBJECTS_PATH_PREFIX = "/v1/dashboard/subjects";
/** @deprecated Use {@link DASHBOARD_SUBJECTS_PATH_PREFIX}. */
export const DASHBOARD_SUBJECT_REMINDER_PATH_PREFIX =
  DASHBOARD_SUBJECTS_PATH_PREFIX;

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return null;
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
}

function withDefinedParams(params) {
  const entries = Object.entries(params).filter(([, value]) => value != null);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/**
 * Reads the authorised dashboard subject list from the regional results
 * service.
 *
 * @param {{ limit?: number, signal?: AbortSignal }} [options={}]
 * @returns {Promise<{ subjects: object[], totalCount: number, hasMore: boolean, limit: number }>}
 */
export async function fetchDashboardSubjects({ limit, signal } = {}) {
  const { data } = await api.get(DASHBOARD_RESULTS_SUBJECTS_PATH, {
    params: withDefinedParams({
      limit: Number.isFinite(limit) ? limit : null,
    }),
    signal,
  });

  return data;
}

/**
 * Reads one authorised dashboard subject detail payload.
 *
 * @param {{
 *   subjectKey?: string,
 *   sid?: string,
 *   uid?: string,
 *   measurementLimit?: number,
 *   questionnaireLimit?: number,
 *   allMeasurements?: boolean,
 *   allQuestionnaires?: boolean,
 *   signal?: AbortSignal
 * }} [options={}]
 * @returns {Promise<object>}
 */
export async function fetchDashboardSubjectDetail({
  subjectKey,
  sid,
  uid,
  measurementLimit,
  measurementOffset,
  questionnaireLimit,
  allMeasurements,
  allQuestionnaires,
  signal,
} = {}) {
  const { data } = await api.get(DASHBOARD_RESULTS_SUBJECT_DETAIL_PATH, {
    params: withDefinedParams({
      subjectKey: normalizeOptionalString(subjectKey),
      sid: normalizeOptionalString(sid),
      uid: normalizeOptionalString(uid),
      measurementLimit: Number.isFinite(measurementLimit)
        ? measurementLimit
        : null,
      measurementOffset:
        Number.isFinite(measurementOffset) && measurementOffset > 0
          ? measurementOffset
          : null,
      questionnaireLimit: Number.isFinite(questionnaireLimit)
        ? questionnaireLimit
        : null,
      allMeasurements: allMeasurements === true ? true : null,
      allQuestionnaires: allQuestionnaires === true ? true : null,
    }),
    signal,
  });

  return data;
}

/**
 * Reads one authorised dashboard recording detail payload.
 *
 * @param {{
 *   uid: string,
 *   docId: string,
 *   variant?: string,
 *   signalAlgorithm?: "mpm" | "pca",
 *   signal?: AbortSignal
 * }} options
 * @returns {Promise<object>}
 */
export async function fetchDashboardRecordingDetail({
  uid,
  docId,
  variant,
  signalAlgorithm,
  signal,
} = {}) {
  const normalizedUid = normalizeOptionalString(uid);
  const normalizedDocId = normalizeOptionalString(docId);

  if (!normalizedUid || !normalizedDocId) {
    throw new Error("A uid and docId are required to load recording detail.");
  }

  const { data } = await api.get(
    `${DASHBOARD_RESULTS_RECORDINGS_PATH_PREFIX}/${encodeURIComponent(
      normalizedUid,
    )}/${encodeURIComponent(normalizedDocId)}`,
    {
      params: withDefinedParams({
        variant: normalizeOptionalString(variant),
        signalAlgorithm: normalizeOptionalString(signalAlgorithm),
      }),
      signal,
    },
  );

  return data;
}

// The export scans every authorised subject, so it can take far longer than a
// normal read; give it a generous client-side timeout to match the gateway.
const DASHBOARD_EXPORT_TIMEOUT_MS = 120_000;

/**
 * Reads the full authorised patient-event export as a flat list of rows. The
 * caller turns these rows into a spreadsheet client-side; the backend owns the
 * data (previously the exportAllPatientEventsExcel Cloud Function).
 *
 * @param {{ signal?: AbortSignal }} [options={}]
 * @returns {Promise<{
 *   organisationId: string | null,
 *   studyCodes: string[],
 *   rowCount: number,
 *   rows: object[],
 * }>}
 */
export async function fetchDashboardEventsExport({ signal } = {}) {
  const { data } = await api.get(DASHBOARD_RESULTS_EXPORT_PATH, {
    timeout: DASHBOARD_EXPORT_TIMEOUT_MS,
    signal,
  });

  return data;
}

/**
 * Sends one clinician-triggered reminder email for the requested dashboard
 * subject. The backend still decides whether the reminder is actually sent or
 * skipped (for example, when the patient has opted out or has nothing due).
 *
 * @param {{ uid: string }} options
 * @returns {Promise<object>}
 */
export async function sendDashboardSubjectReminder({ uid } = {}) {
  const normalizedUid = normalizeOptionalString(uid);

  if (!normalizedUid) {
    throw new Error("A uid is required to send a dashboard reminder.");
  }

  const { data } = await api.post(
    `${DASHBOARD_SUBJECTS_PATH_PREFIX}/${encodeURIComponent(
      normalizedUid,
    )}/reminder-email`,
    {},
  );

  return data;
}

/**
 * Reads the stored demographic profile a patient entered during onboarding (and
 * has since been able to edit) for one authorised dashboard subject.
 *
 * @param {{ uid: string, signal?: AbortSignal }} options
 * @returns {Promise<{ subject: object, profile: object, fields: string[] }>}
 */
export async function fetchDashboardSubjectProfile({ uid, signal } = {}) {
  const normalizedUid = normalizeOptionalString(uid);

  if (!normalizedUid) {
    throw new Error("A uid is required to load a subject profile.");
  }

  const { data } = await api.get(
    `${DASHBOARD_SUBJECTS_PATH_PREFIX}/${encodeURIComponent(
      normalizedUid,
    )}/profile`,
    { signal },
  );

  return data;
}

/**
 * Reads the per-field profile timeline for one authorised dashboard subject.
 *
 * Entries are change points ordered newest first, so a field the patient has
 * never revised comes back as a single entry.
 *
 * @param {{ uid: string, signal?: AbortSignal }} options
 * @returns {Promise<{
 *   fields: Record<string, Array<{ recordedAt: string | null, value: unknown, isCurrent: boolean }>>,
 *   snapshotCount: number,
 *   truncated: boolean,
 *   unreadableSnapshotCount: number
 * }>}
 */
export async function fetchDashboardSubjectProfileHistory({ uid, signal } = {}) {
  const normalizedUid = normalizeOptionalString(uid);

  if (!normalizedUid) {
    throw new Error("A uid is required to load a subject profile history.");
  }

  const { data } = await api.get(
    `${DASHBOARD_SUBJECTS_PATH_PREFIX}/${encodeURIComponent(
      normalizedUid,
    )}/profile/history`,
    { signal },
  );

  return data;
}
